/**
 * Reusable 2–3-option sliding pill toggle.
 * Thumb slides via transform translateX only (ease-out, no bounce, no width animation).
 * Generic props: options, value, onChange.
 * Used e.g. by the public landing nav for NL/EN.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

export type SegmentOption<T extends string> = {
  value: T;
  label: string;
  icon?: ReactNode;
};

type Props<T extends string> = {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Accessible label for the group */
  ariaLabel?: string;
};

export function SegmentedToggle<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: Props<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [thumbStyle, setThumbStyle] = useState<{
    width: number;
    translateX: number;
  }>({ width: 0, translateX: 0 });

  const activeIndex = options.findIndex((o) => o.value === value);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    function measure() {
      const btn = buttonRefs.current[activeIndex];
      const first = buttonRefs.current[0];
      if (!btn || !first) return;
      const btnRect = btn.getBoundingClientRect();
      const firstRect = first.getBoundingClientRect();
      setThumbStyle({
        width: btnRect.width,
        translateX: btnRect.left - firstRect.left,
      });
    }

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [activeIndex]);

  return (
    <div
      ref={containerRef}
      role="group"
      aria-label={ariaLabel}
      className="relative inline-flex bg-[var(--panel-soft)] border border-[var(--border)] rounded-full p-1 select-none"
    >
      {/* Sliding thumb — transform only, ease-out, no bounce */}
      <span
        aria-hidden="true"
        className="absolute top-1 bottom-1 left-1 rounded-full bg-[var(--panel)] pointer-events-none"
        style={{
          width: thumbStyle.width,
          transform: `translateX(${thumbStyle.translateX}px)`,
          // Why: only transform animates — no width/height in transition.
          // Width is set instantaneously via the style prop (no transition
          // property for it at all), so the thumb never stretches between
          // options of different widths. Per brand.md motion rules.
          transition: "transform 0.22s ease-out",
          boxShadow:
            "0 1px 2px rgba(0,0,0,.08), 0 6px 14px -8px rgba(0,0,0,.30)",
        }}
      />

      {options.map((opt, i) => (
        <button
          key={opt.value}
          ref={(el) => {
            buttonRefs.current[i] = el;
          }}
          type="button"
          role="radio"
          aria-checked={opt.value === value}
          onClick={() => onChange(opt.value)}
          className={[
            "relative z-10 inline-flex items-center gap-1.5 border-0 bg-transparent cursor-pointer",
            "font-semibold text-[13.5px] px-[13px] py-2 rounded-full whitespace-nowrap",
            "transition-colors duration-200",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--accent)] focus-visible:outline-offset-2",
            "min-h-[36px]",
            opt.value === value
              ? "text-[var(--accent)]"
              : "text-[var(--ink-muted)]",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {opt.icon}
          {opt.label}
        </button>
      ))}
    </div>
  );
}
